import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import { Agent } from "@/api/generated";
import theme from "@/theme";
import { hasUsableAgentId } from "@/utilities/agentId";
import { buildAgentName } from "@/utilities/agentName";

import { CompatibilityScoreDisplay } from "../agent/CompatibilityScoreDisplay";
import { CustomButton } from "../Button";
import { CompatibilityRadarChart, hasCompatibilityRadarScores } from "../compatibility_radar_chart/CompatibilityRadarChart";

import { CertifiedPlannerBadge } from "./CertifiedPlannerBadge";

const useStyles = makeStyles(() => ({
  featured: {
    display: "flex", flexDirection: "column", borderRadius: 16, overflow: "hidden",
    backgroundColor: theme.palette.semantic["surface/card"], boxShadow: "0 2px 8px rgba(22,32,46,0.06)",
    scrollSnapAlign: "start", minWidth: 0,
  },
  profile: {
    padding: "8px 20px 16px", display: "flex", flexDirection: "column", gap: 8,
    background: `linear-gradient(100deg, ${theme.palette.semantic["surface/hero-start"]}, ${theme.palette.semantic["surface/trust"]})`,
  },
  identity: { display: "flex", alignItems: "center", gap: 16 },
  portrait: {
    width: 96, height: 96, borderRadius: 32, overflow: "hidden", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 500,
    color: theme.palette.semantic["action/primary"], backgroundColor: theme.palette.semantic["state/selected-bg"],
    "& img": { display: "block", width: "100%", height: "100%", objectFit: "cover" },
  },
  portraitSmall: { width: 44, height: 44, borderRadius: 22, fontSize: 16 },
  names: { minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 8 },
  name: { fontSize: 20, fontWeight: 700, lineHeight: 1.5, color: theme.palette.semantic["text/inverse"], overflowWrap: "anywhere" },
  company: { fontSize: 14, lineHeight: 1.5, color: theme.palette.semantic["text/inverse"], overflowWrap: "anywhere" },
  logo: {
    width: 96, height: 24, padding: "3px 8px", backgroundColor: theme.palette.semantic["surface/card"],
    display: "flex", alignItems: "center", justifyContent: "center",
    "& img": { display: "block", maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
  },
  scoreBand: {
    backgroundColor: theme.palette.semantic["surface/card"], borderRadius: 8, padding: 12,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap",
  },
  scoreLabel: { fontSize: 14, lineHeight: 1.5, fontWeight: 700, color: theme.palette.semantic["text/heading"] },
  body: { padding: 20, display: "flex", flexDirection: "column", gap: 16, flex: 1 },
  chart: { width: "100%", minHeight: 244.154, display: "flex", alignItems: "center", justifyContent: "center" },
  unavailable: { fontSize: 12, lineHeight: 1.5, color: theme.palette.semantic["text/secondary"] },
  commentLabel: {
    display: "inline-block", borderRadius: 4, padding: "4px 8px", marginBottom: 8, fontSize: 12, fontWeight: 700, lineHeight: 1.5,
    color: theme.palette.semantic["action/primary"], backgroundColor: theme.palette.semantic["state/selected-bg"],
  },
  comment: { fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: theme.palette.semantic["text/primary"] },
  action: { marginTop: "auto" },
  listCard: {
    width: "100%", padding: "16px 8px 16px 16px", borderRadius: 16, display: "flex", alignItems: "center", gap: 12,
    textAlign: "left", backgroundColor: theme.palette.semantic["surface/card"], boxShadow: "0 2px 4px rgba(22,32,46,0.06)",
    "&:focus-visible": { outline: `2px solid ${theme.palette.semantic["border/focus"]}`, outlineOffset: 2 },
  },
  listContent: { display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 0 },
  listIdentity: { display: "flex", alignItems: "center", gap: 8 },
  listNames: { flex: 1, minWidth: 0, marginLeft: 4 },
  listName: { fontSize: 16, lineHeight: 1.5, fontWeight: 700, overflowWrap: "anywhere" },
  listCompany: { fontSize: 12, lineHeight: 1.5, marginTop: 4, color: theme.palette.semantic["text/secondary"], overflowWrap: "anywhere" },
  listScore: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, flexShrink: 0 },
  listScoreLabel: { fontSize: 12, lineHeight: 1.5, color: theme.palette.semantic["text/secondary"], whiteSpace: "nowrap" },
  chevron: { display: "block", flexShrink: 0 },
}));

const displayName = (agent: Agent) => buildAgentName(agent).trim() || "プランナー";
const hasScore = (agent: Agent) => hasCompatibilityRadarScores(agent.compatibility_radar?.scores)
  && typeof agent.compatibility_radar?.total_score === "number" && Number.isFinite(agent.compatibility_radar.total_score);

const PlannerPortrait: React.FC<{ agent: Agent; small?: boolean }> = ({ agent, small = false }) => {
  const classes = useStyles();
  const url = agent.agent_profile_image?.image_url;
  const [failedUrl, setFailedUrl] = React.useState<string>();
  return (
    <Box className={`${classes.portrait} ${small ? classes.portraitSmall : ""}`} aria-hidden="true">
      {url && failedUrl !== url
        ? <img src={url} alt="" onError={() => setFailedUrl(url)} />
        : displayName(agent).slice(0, 1)}
    </Box>
  );
};

const CompanyLogo: React.FC<{ agent: Agent }> = ({ agent }) => {
  const classes = useStyles();
  const url = agent.company?.company_logo_image?.image_url;
  const [failedUrl, setFailedUrl] = React.useState<string>();
  if (!url || failedUrl === url) return null;
  return <Box className={classes.logo}><img src={url} alt={agent.company?.name || "会社ロゴ"} onError={() => setFailedUrl(url)} /></Box>;
};

const PlannerComment: React.FC<{ agent: Agent }> = ({ agent }) => {
  const classes = useStyles();
  const comment = agent.propally_comment?.comment;
  if (!comment?.trim()) return null;
  return (
    <Box>
      <Typography component="span" className={classes.commentLabel}>プロパリーからのコメント</Typography>
      <Typography className={classes.comment}>{comment}</Typography>
    </Box>
  );
};

/**
 * 上位3人向けの大判カード。登録導線（診断結果）・条件なしのプランナー一覧・ホームで共用する。
 * rank は呼び出し元が API の並び順から決める（王冠は CompatibilityScoreDisplay が rank 1〜3 にだけ出す）。
 * ホームは相性グラフを省いた形で出す（showRadarChart=false）。
 */
export const FeaturedPlannerCard: React.FC<{ agent: Agent; rank: number; showRadarChart?: boolean; onDetail: () => void }> = ({ agent, rank, showRadarChart = true, onDetail }) => {
  const classes = useStyles();
  const valid = hasUsableAgentId(agent);
  return (
    <article className={classes.featured} data-testid="featured-planner-card" data-rank={rank} data-agent-id={agent.id} aria-label={`${displayName(agent)}のおすすめカード`} onClick={valid ? onDetail : undefined}>
      <Box className={classes.profile}>
        <CertifiedPlannerBadge surface="dark" />
        <Box className={classes.identity}>
          <PlannerPortrait agent={agent} />
          <Box className={classes.names}>
            <CompanyLogo agent={agent} />
            <Typography component="h3" className={classes.name}>{displayName(agent)}</Typography>
            {agent.company?.name && <Typography className={classes.company}>{agent.company.name}</Typography>}
          </Box>
        </Box>
        <Box className={classes.scoreBand}>
          {hasScore(agent) ? <>
            <Typography className={classes.scoreLabel}>あなたとの相性スコア</Typography>
            <CompatibilityScoreDisplay score={agent.compatibility_radar.total_score} rank={rank} size="L" crownSize={24} />
          </> : <Typography className={classes.unavailable}>相性スコアは準備中です</Typography>}
        </Box>
      </Box>
      <Box className={classes.body}>
        {showRadarChart && <Box className={classes.chart}>
          {hasCompatibilityRadarScores(agent.compatibility_radar?.scores)
            ? <CompatibilityRadarChart scores={agent.compatibility_radar.scores} variant="featured" />
            : <Typography className={classes.unavailable}>相性グラフは準備中です</Typography>}
        </Box>}
        <PlannerComment agent={agent} />
        <Box className={classes.action}>
          <CustomButton tone="trust" buttonSize="L" fullWidth disabled={!valid} onClick={(event) => { event.stopPropagation(); onDetail(); }}>
            {agent.is_matched ? "マッチ済みのプランナーを見る" : "このプランナーの詳細を見る"}
          </CustomButton>
        </Box>
      </Box>
    </article>
  );
};

/** 4人目以降の一覧カード。大判カードと同じ画面に並ぶ前提で、王冠と相性グラフは出さない */
export const PlannerListCard: React.FC<{ agent: Agent; rank: number; onDetail: () => void }> = ({ agent, rank, onDetail }) => {
  const classes = useStyles();
  return (
    <ButtonBase className={classes.listCard} onClick={onDetail} disabled={!hasUsableAgentId(agent)} data-testid="planner-list-card" data-rank={rank} data-agent-id={agent.id} aria-label={`${displayName(agent)}の詳細を見る`}>
      <Box className={classes.listContent}>
        <CertifiedPlannerBadge />
        <Box className={classes.listIdentity}>
          <PlannerPortrait agent={agent} small />
          <Box className={classes.listNames}>
            <Typography component="h3" className={classes.listName}>{displayName(agent)}</Typography>
            {agent.company?.name && <Typography className={classes.listCompany}>{agent.company.name}</Typography>}
          </Box>
          {hasScore(agent) && <Box className={classes.listScore}>
            <Typography className={classes.listScoreLabel}>相性スコア</Typography>
            <CompatibilityScoreDisplay score={agent.compatibility_radar.total_score} showCrown={false} tone="default" />
          </Box>}
        </Box>
        <PlannerComment agent={agent} />
      </Box>
      <img className={classes.chevron} src="/icons/planner_onboarding/chevron.svg" alt="" />
    </ButtonBase>
  );
};
